import type { NewPainEntry } from './model';

const DATABASE_NAME = 'cynthia-health-local';
const STORE_NAME = 'outbox';
const STORAGE_TIMEOUT_MS = 4_000;

function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, 1);
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      reject(new Error('Phone storage did not become ready in time.'));
    }, STORAGE_TIMEOUT_MS);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => {
      clearTimeout(timeout);
      if (timedOut) request.result.close();
      else resolve(request.result);
    };
    request.onerror = () => {
      clearTimeout(timeout);
      reject(request.error);
    };
    request.onblocked = () => {
      clearTimeout(timeout);
      reject(new Error('Phone storage is temporarily busy.'));
    };
  });
}

async function transaction<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
) {
  const database = await openDatabase();
  return new Promise<T>((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, mode);
    const request = operation(tx.objectStore(STORE_NAME));
    let result: T;
    let settled = false;
    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      database.close();
      reject(error);
    };
    const timeout = setTimeout(() => {
      try { tx.abort(); } catch { /* The transaction may already be closing. */ }
      fail(new Error('Phone storage did not finish in time.'));
    }, STORAGE_TIMEOUT_MS);
    request.onsuccess = () => { result = request.result; };
    request.onerror = () => fail(request.error);
    tx.oncomplete = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      database.close();
      resolve(result);
    };
    tx.onerror = () => fail(tx.error);
    tx.onabort = () => fail(tx.error ?? new Error('The offline save was interrupted.'));
  });
}

export function queueEntry(entry: NewPainEntry) {
  return transaction('readwrite', (store) => store.put(entry));
}

export function removeQueuedEntry(id: string) {
  return transaction('readwrite', (store) => store.delete(id));
}

export function listQueuedEntries() {
  return transaction<NewPainEntry[]>('readonly', (store) => store.getAll());
}
